import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icons, MenuItem, MenuList, MenuRow, Popover } from '@/shared/ui';

import styles from './CreateMenu.module.css';

/** One thing the viewer can create. New object types are just more items. */
export interface CreateItem {
  id: string;
  label: string;
  icon: ReactNode;
  onSelect: () => void;
  /** A rule above the item (starts a new group). */
  separated?: boolean;
}

/**
 * The ONE "Create" dropdown of the product (home and workspace header): a single prominent
 * button and a list of what can be created. The caller lists only what the viewer may do, with
 * the context (workspace) already decided; with nothing to offer the button is not drawn.
 */
export function CreateMenu({ items }: { items: readonly CreateItem[] }) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <Popover
      label={t('home.createMenu')}
      width={280}
      alignEnd
      trigger={(props, open) => (
        <button
          type="button"
          {...props}
          className={styles.createButton}
          data-open={open ? 'true' : undefined}
          data-testid="create-menu"
        >
          <Icons.plus /> {t('home.create')}
        </button>
      )}
    >
      {(close) => (
        <MenuList>
          {items.map((item) => (
            <MenuRow key={item.id} separated={item.separated ?? false}>
              <MenuItem
                icon={item.icon}
                onClick={() => {
                  close();
                  item.onSelect();
                }}
              >
                {item.label}
              </MenuItem>
            </MenuRow>
          ))}
        </MenuList>
      )}
    </Popover>
  );
}
